import React from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import CardHeading from "./CardHeading";
import { NO_READING } from "./overviewFormat";

/**
 * A card of the overview, with the three states every section has: on its way
 * (`undefined`), not readable (`null` → "No reading", never an empty card or a stale
 * number, I5), and read.
 */
export default function OverviewCard<T>({
    title,
    subtitle,
    fill = false,
    section,
    aside,
    children,
}: {
    title: string;
    /** One line under the title, for what the numbers are measured against. */
    subtitle?: string;
    /** Fill the height of its grid row, to match the card beside it; its body may then fill what is left. */
    fill?: boolean;
    section: T | null | undefined;
    /** Right of the title, for a one-line summary ("29 errors in the last 24 h"). */
    aside?: (section: T) => React.ReactNode;
    children: (section: T) => React.ReactNode;
}) {
    return (
        <GenericCard isCollapsible={false} variant="primary" shadow="sm" className={fill ? "h-full" : undefined}>
            <section
                className={`p-5 ${fill ? "flex flex-col flex-1 min-h-0" : ""}`}
                aria-label={title}
                aria-busy={section === undefined}
            >
                <div className="flex items-baseline justify-between gap-4 mb-3">
                    <CardHeading title={title} subtitle={subtitle} />
                    {section != null && aside ? (
                        <span className="text-sm text-semantic-text-secondary">{aside(section)}</span>
                    ) : null}
                </div>
                <OverviewCardBody section={section}>{children}</OverviewCardBody>
            </section>
        </GenericCard>
    );
}

function OverviewCardBody<T>({
    section,
    children,
}: {
    section: T | null | undefined;
    children: (section: T) => React.ReactNode;
}) {
    if (section === undefined) {
        return (
            <div className="flex flex-col gap-3">
                <SkeletonBlock h={16} w="80%" className="rounded" />
                <SkeletonBlock h={16} w="60%" className="rounded" />
                <SkeletonBlock h={16} w="70%" className="rounded" />
            </div>
        );
    }
    if (section === null) {
        return <p className="text-sm text-semantic-text-secondary">{NO_READING}</p>;
    }
    return <>{children(section)}</>;
}
